var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import FocusLock from 'react-focus-lock';
import '../styles/popup.scss';
var Popup = function (props) {
    var isOpen = props.isOpen, onClose = props.onClose, children = props.children, ariaLabel = props.ariaLabel, ariaLabelledBy = props.ariaLabelledBy, ariaDescribedBy = props.ariaDescribedBy, _a = props.className, className = _a === void 0 ? '' : _a, onClick = props.onClick, returnFocusTo = props.returnFocusTo, initialFocusRef = props.initialFocusRef;
    var _b = useState(isOpen), popupOpen = _b[0], setPopupOpen = _b[1];
    // For always-mounted popups (ManipulativePopup):
    // capture trigger when isOpen transitions from false→true, BEFORE children render.
    useEffect(function () {
        if (isOpen && !popupOpen) {
            prevFocusRef.current = document.activeElement;
        }
        setPopupOpen(isOpen);
    }, [isOpen]);
    var popupRef = useRef(null);
    var overlayRef = useRef(null);
    var prevFocusRef = useRef(null);
    // For conditionally-rendered popups (InsertTablePopup, SplitFractionTileModal):
    // capture trigger in useLayoutEffect which runs BEFORE any child useEffect
    // (like InputBox autoFocus). Only captures if not already set by the isOpen effect.
    useLayoutEffect(function () {
        if (popupOpen && !prevFocusRef.current) {
            prevFocusRef.current = document.activeElement;
        }
    }, [popupOpen]);
    // When popup opens: focus dialog container so screen reader announces it.
    // When popup closes (or unmounts while open): restore focus to trigger.
    useEffect(function () {
        if (popupOpen) {
            // Focus the first interactive element so JAWS enters forms mode immediately
            // (arrow keys work without needing Tab first). Dialog name is still announced
            // via role="dialog" + aria-modal="true" when any element inside receives focus.
            var timerId_1 = setTimeout(function () {
                var _a, _b;
                if (initialFocusRef === null || initialFocusRef === void 0 ? void 0 : initialFocusRef.current) {
                    initialFocusRef.current.focus();
                }
                else {
                    var firstFocusable = (_a = popupRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]');
                    if (firstFocusable) {
                        firstFocusable.focus();
                    }
                    else {
                        (_b = popupRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                }
            }, 50);
            return function () {
                var _a;
                clearTimeout(timerId_1);
                // Restore focus: prefer explicit returnFocusTo, fall back to the captured trigger
                var elementToFocus = (_a = returnFocusTo === null || returnFocusTo === void 0 ? void 0 : returnFocusTo.current) !== null && _a !== void 0 ? _a : prevFocusRef.current;
                if (elementToFocus && document.body.contains(elementToFocus)) {
                    setTimeout(function () { return elementToFocus.focus(); }, 0);
                }
                prevFocusRef.current = null;
            };
        }
    }, [popupOpen]);
    // Make background content inert when popup is open (WCAG 2.4.11)
    useEffect(function () {
        if (!popupOpen)
            return;
        var overlay = overlayRef.current;
        if (!overlay)
            return;
        // Find all sibling elements of the overlay's parent chain up to body
        // and set inert + aria-hidden on them
        var inertedElements = [];
        var setInert = function (el) {
            var parent = el.parentElement;
            if (!parent || parent === document.documentElement)
                return;
            Array.from(parent.children).forEach(function (sibling) {
                if (sibling === el || !(sibling instanceof HTMLElement))
                    return;
                if (sibling.tagName === 'SCRIPT' ||
                    sibling.tagName === 'STYLE' ||
                    sibling.tagName === 'LINK')
                    return;
                if (!sibling.hasAttribute('inert')) {
                    sibling.setAttribute('inert', '');
                    sibling.setAttribute('aria-hidden', 'true');
                    inertedElements.push(sibling);
                }
            });
            // Walk up to handle nested containers
            if (parent !== document.body) {
                setInert(parent);
            }
        };
        setInert(overlay);
        return function () {
            inertedElements.forEach(function (el) {
                el.removeAttribute('inert');
                el.removeAttribute('aria-hidden');
            });
        };
    }, [popupOpen]);
    var handleKeyDown = useCallback(function (event) {
        if (event.key === 'Escape')
            onClose();
    }, [onClose]);
    if (!popupOpen)
        return null;
    // Determine accessible name: prefer ariaLabelledBy > ariaLabel > fallback
    var dialogLabelProps = {};
    if (ariaLabelledBy) {
        dialogLabelProps['aria-labelledby'] = ariaLabelledBy;
    }
    else {
        dialogLabelProps['aria-label'] = ariaLabel || 'Dialog';
    }
    if (ariaDescribedBy) {
        dialogLabelProps['aria-describedby'] = ariaDescribedBy;
    }
    return createPortal(_jsx("div", { className: "popupOverlay ".concat(className), ref: overlayRef, onClick: function (e) {
            setPopupOpen(false);
            onClose();
        }, children: _jsx(FocusLock, { autoFocus: false, returnFocus: false, children: _jsx("div", __assign({ className: "popup", ref: popupRef, tabIndex: 0, role: "dialog", "aria-modal": "true" }, dialogLabelProps, { onKeyDown: handleKeyDown, onClick: function (e) {
                    e.stopPropagation();
                    onClick && onClick(e);
                }, children: _jsx("div", { className: "popupContent", children: children }) })) }) }), document.body);
};
export default Popup;
